import { z } from "zod";

export const TitleOutputSchema = z.object({
  title: z
    .string()
    .min(1)
    .describe("A short, concise, complete title for the chat conversation")
});

export const ProjectContextSummaryOutputSchema = z.object({
  summary: z
    .string()
    .max(4000)
    .describe(
      "A compact project-wide memory with known facts, assumptions, open questions, and recent conclusions"
    )
});

export const TaxAdvisorOutputSchema = z
  .object({
    type: z
      .enum(["counter_question", "answer"])
      .describe(
        'Response type: "counter_question" if more info is needed, "answer" if the question can be answered with sources'
      ),
    content: z
      .string()
      .describe(
        "The response text: either follow-up questions or the full answer with inline legal citations"
      ),
    sources: z
      .array(
        z
          .string()
          .describe(
            'A specific legal citation, e.g. "Art. 18 Abs. 1 DBG (SR 642.11)", "§ 1 Abs. 1 EStG", "Art. 15 DBA Deutschland-Schweiz", "Art. 11 VO (EG) 883/2004", "BGE 143 II 459" or "Urteil 2C_1/2023"'
          )
      )
      .nullable()
      .describe(
        'REQUIRED for type="answer": array of all cited legal sources. MUST NOT be empty or null when type is "answer". Only null when type is "counter_question".'
      )
  })
  .superRefine((value, ctx) => {
    if (value.type === "answer") {
      if (!value.sources || value.sources.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["sources"],
          message:
            'sources must contain at least one citation when type is "answer"'
        });
        return;
      }
      const hasBlank = value.sources.some((s) => s.trim().length === 0);
      if (hasBlank) {
        ctx.addIssue({
          code: "custom",
          path: ["sources"],
          message: "sources must not contain blank strings"
        });
      }
    } else if (value.sources && value.sources.length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["sources"],
        message: 'sources must be null or empty when type is "counter_question"'
      });
    }
  });

export type TitleOutput = z.infer<typeof TitleOutputSchema>;
export type ProjectContextSummaryOutput = z.infer<
  typeof ProjectContextSummaryOutputSchema
>;
export type TaxAdvisorOutput = z.infer<typeof TaxAdvisorOutputSchema>;
